import React, { useState, useMemo } from 'react';
import { useConversations } from '../context/ConversationsContext';
import { Conversation } from '../types/conversation';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  User,
  Bot,
  Clock,
  TrendingUp,
  FileText,
  ExternalLink,
  CheckCircle2,
  XCircle,
  MessageCircle,
  AlertCircle,
  Filter,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

type ReviewFilter = 'all' | 'review-required' | 'pending' | 'approved' | 'rejected' | 'clarification-requested';

export const ConversationsPage: React.FC = () => {
  const { conversations } = useConversations();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [personFilter, setPersonFilter] = useState<string>('all');

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getConfidenceColor = (level?: number) => {
    if (!level) return 'text-muted-foreground';
    if (level >= 80) return 'text-green-600 dark:text-green-400';
    if (level >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-orange-600 dark:text-orange-400';
  };

  // Get unique persons for filter
  const uniquePersons = useMemo(() => {
    const persons = new Set<string>();
    conversations.forEach(conv => {
      if (conv.askedBy) persons.add(conv.askedBy);
    });
    return Array.from(persons).sort();
  }, [conversations]);

  // Filter conversations
  const filteredConversations = useMemo(() => {
    let filtered = conversations;

    // Apply review filter
    if (reviewFilter !== 'all') {
      if (reviewFilter === 'review-required') {
        filtered = filtered.filter(conv => conv.reviewRequested && conv.reviewStatus === 'pending');
      } else {
        filtered = filtered.filter(conv => conv.reviewStatus === reviewFilter);
      }
    }

    // Apply person filter
    if (personFilter !== 'all') {
      filtered = filtered.filter(conv => conv.askedBy === personFilter);
    }

    return filtered;
  }, [conversations, reviewFilter, personFilter]);

  const getReviewStatusBadge = (conversation: Conversation) => {
    if (!conversation.reviewRequested) return null;

    switch (conversation.reviewStatus) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      case 'clarification-requested':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
            <MessageCircle className="h-3 w-3 mr-1" />
            Clarification Requested
          </Badge>
        );
      default:
        return null;
    }
  };

  const hasActiveFilters = reviewFilter !== 'all' || personFilter !== 'all';

  return (
    <div className="flex flex-col h-screen">
      <div className="p-6 border-b bg-background">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold mb-2">Conversation History</h1>
            <p className="text-sm text-muted-foreground">
              {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''}
              {hasActiveFilters && ` (filtered from ${conversations.length} total)`}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filters:</span>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground whitespace-nowrap">Review Status:</label>
            <Select value={reviewFilter} onValueChange={(value) => setReviewFilter(value as ReviewFilter)}>
              <SelectTrigger className="w-[180px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Conversations</SelectItem>
                <SelectItem value="review-required">Review Required</SelectItem>
                <SelectItem value="pending">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="clarification-requested">Clarification Requested</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground whitespace-nowrap">Asked By:</label>
            <Select value={personFilter} onValueChange={setPersonFilter}>
              <SelectTrigger className="w-[150px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All People</SelectItem>
                {uniquePersons.map((person) => (
                  <SelectItem key={person} value={person}>
                    {person}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setReviewFilter('all');
                setPersonFilter('all');
              }}
              className="h-9"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl mx-auto space-y-4">
          {filteredConversations.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Bot className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">
                  {hasActiveFilters ? 'No conversations match your filters' : 'No conversations yet'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {hasActiveFilters
                    ? 'Try adjusting your filters or clear them to see all conversations'
                    : 'Start chatting to see conversation history here'}
                </p>
              </div>
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <Card
                key={conversation.id}
                className="p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setExpandedId(expandedId === conversation.id ? null : conversation.id)}
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">
                          {conversation.title}
                        </h3>
                        {getReviewStatusBadge(conversation)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          <span>{conversation.askedBy}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{formatDate(conversation.answeredAt)}</span>
                        </div>
                        {conversation.mentionedPerson && (
                          <div className="flex items-center gap-1">
                            <Bot className="h-4 w-4" />
                            <span>Mentioned: {conversation.mentionedPerson}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Question */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Question:</p>
                    <p className="text-sm text-foreground">{conversation.question}</p>
                  </div>

                  {/* Answer Preview */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Answer:</p>
                    <p className={cn(
                      "text-sm text-foreground",
                      !expandedId || expandedId !== conversation.id ? "line-clamp-2" : ""
                    )}>
                      {conversation.answer}
                    </p>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === conversation.id && (
                    <>
                      <Separator />

                      {/* Confidence Level - only show if there's an actual answer */}
                      {conversation.confidenceLevel !== undefined &&
                        !conversation.answer.toLowerCase().includes("can't answer this question confidently") && (
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs font-medium text-muted-foreground">Confidence:</span>
                            <span className={cn("text-xs font-semibold", getConfidenceColor(conversation.confidenceLevel))}>
                              {conversation.confidenceLevel >= 80 ? 'High' :
                                conversation.confidenceLevel >= 60 ? 'Medium' : 'Low'} ({conversation.confidenceLevel}%)
                            </span>
                          </div>
                        )}


                      {/* Sources */}
                      {conversation.sources && conversation.sources.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2">Sources:</p>
                          <div className="flex flex-col gap-2">
                            {conversation.sources.map((source, idx) => (
                              <a
                                key={idx}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent/50 transition-colors group text-sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FileText className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                                <span className="text-foreground group-hover:text-primary flex-1">
                                  {source.title}
                                </span>
                                <span className="text-xs text-muted-foreground">({source.type})</span>
                                <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-foreground" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Review Info */}
                      {conversation.reviewRequested && (
                        <div className="p-3 rounded-md bg-muted">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            Review Status:
                          </p>
                          <p className="text-sm text-foreground">
                            {conversation.reviewStatus === 'pending' && 'Awaiting human review'}
                            {conversation.reviewStatus === 'approved' && 'Approved by reviewer'}
                            {conversation.reviewStatus === 'rejected' && 'Rejected by reviewer'}
                            {conversation.reviewStatus === 'clarification-requested' && 'Clarification requested from user'}
                          </p>
                          {conversation.reviewId && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Review ID: {conversation.reviewId}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

